import { type FC, type FormEvent } from 'react';

import {
  IconLoader2,
  IconSettingsPlus,
} from '@tabler/icons-react';
import { ButtonField } from '@wk/components-react16';

import type {
  SiteComplianceConfiguration,
  SiteComplianceSite,
} from '@/domains/siteCompliance';
import ComboBox from '@/shared/ui/Combobox';

interface SiteComplianceOnboardConfigureFormProps {
  isLoadingSites: boolean;
  latestConfiguration: SiteComplianceConfiguration | null;
  onSiteSearchChange: (value: string) => void;
  onSelectedSiteChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  selectedSiteId: string;
  sites: SiteComplianceSite[];
}

const SiteComplianceOnboardConfigureForm: FC<
  SiteComplianceOnboardConfigureFormProps
> = ({
  isLoadingSites,
  latestConfiguration,
  onSiteSearchChange,
  onSelectedSiteChange,
  onSubmit,
  selectedSiteId,
  sites,
}) => {
  const comboBoxOptions = sites.map((site) => ({
    label: (
      <span className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          {site.siteId} - {site.siteName}
        </span>
        <span className="text-xs text-muted-foreground">
          {site.workdayId ?? '—'} · {site.region}
        </span>
      </span>
    ),
    value: site.siteId,
    valueLabel: `${site.siteId} - ${site.siteName}`,
  }));

  const isDraftConfiguration = latestConfiguration?.isDraft ?? false;
  const isMutatingConfiguration = false;

  return (
    <form
      className="onboard-form flex items-center gap-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-3"
      onSubmit={onSubmit}
    >
      <div className="flex flex-col max-sm:w-full">
        <ComboBox
          label=""
          labelFor="site-compliance-sites"
          options={comboBoxOptions}
          disabled={isLoadingSites}
          value={selectedSiteId}
          onChange={onSelectedSiteChange}
          onSearchChange={onSiteSearchChange}
          filter={(value, search) => {
            const site = sites.find(
              (candidateSite) => candidateSite.siteId === value,
            );

            if (!site) {
              return false;
            }

            const searchRegex = new RegExp(search, 'i');

            return (
              searchRegex.test(site.siteId) ||
              searchRegex.test(site.siteName) ||
              searchRegex.test(site.workdayId ?? '')
            );
          }}
          placeholder="Select by Site ID, Site Name, or Workday ID"
          hasLoadingIcon={isLoadingSites}
        />
      </div>
      {!isDraftConfiguration ? (
        <div className="subject-onboarding-form-actions">
          <ButtonField size="large">
            <button
              disabled={
                !selectedSiteId || isLoadingSites || isMutatingConfiguration
              }
            >
              <IconSettingsPlus size={20} />
              <span className="flex items-center gap-2">
                <span>Configure</span>
                {isLoadingSites || isMutatingConfiguration ? (
                  <IconLoader2 size={16} className="animate-spin" />
                ) : null}
              </span>
            </button>
          </ButtonField>
        </div>
      ) : null}
    </form>
  );
};

export default SiteComplianceOnboardConfigureForm;
